import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  useGetAdminBulletinPostsQuery,
  useAdminUpdateBulletinPostMutation,
  useAdminDeleteBulletinPostMutation,
  useAdminPinBulletinPostMutation,
} from "../../../../store/slices/api";
import { SharedTextInput, SharedButton, SharedLoading, SharedFeatureError } from "../../../shared";
import { failedMessage } from "../../../shared/serverReason";
import { CategoryTag, CATEGORIES } from "../../Bulletin/Posts/PostCard";
import { categoryLabel, normalizeCategory } from "../../Bulletin/categories";

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_TITLE_LENGTH = 100;
const MAX_CONTENT_LENGTH = 5000;

function PostRow({ post, individualId }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editContent, setEditContent] = useState(post.content);
  // Normalized, so an old stored name ("coffee & catch-up") preselects its
  // current one instead of a value the list doesn't have.
  const [editCategory, setEditCategory] = useState(normalizeCategory(post.category));
  const [actionError, setActionError] = useState(null);

  const [updatePost, { isLoading: isUpdating }] =
    useAdminUpdateBulletinPostMutation();
  const [deletePost, { isLoading: isDeleting }] =
    useAdminDeleteBulletinPostMutation();
  const [pinPost, { isLoading: isPinning }] =
    useAdminPinBulletinPostMutation();

  const handleStartEdit = () => {
    setEditTitle(post.title);
    setEditContent(post.content);
    setEditCategory(normalizeCategory(post.category));
    setActionError(null);
    setIsEditing(true);
  };

  // Each action says so on screen when it fails; the editor stays open with
  // the changes in it.
  const handleSaveEdit = async () => {
    setActionError(null);
    try {
      await updatePost({
        id: post.id,
        individual_id: individualId,
        title: editTitle,
        content: editContent,
        category: editCategory,
      }).unwrap();
    } catch (err) {
      setActionError(failedMessage(err, "The changes didn't save. Please try again."));
      return;
    }
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setActionError(null);
    try {
      await deletePost({ id: post.id, individual_id: individualId }).unwrap();
      // The list stays up while it refreshes, so close the prompt rather than
      // offer "Yes, Delete" again until the row drops out.
      setShowDeleteConfirm(false);
    } catch (err) {
      setActionError(failedMessage(err, "The post wasn't deleted. Please try again."));
    }
  };

  const handleTogglePin = async () => {
    setActionError(null);
    try {
      await pinPost({
        id: post.id,
        individual_id: individualId,
        is_pinned: !post.is_pinned,
      }).unwrap();
    } catch (err) {
      setActionError(failedMessage(err, "That didn't save. Please try again."));
    }
  };

  const errorLine = actionError && (
    <p role="alert" className="text-[14px] font-body text-red-700 dark:text-red-400">
      {actionError}
    </p>
  );

  const formattedDate = dayjs(post.created_at)
    .utc()
    .local()
    .format("MMM D, YYYY h:mma");

  const attachmentCount = post.attachments ? post.attachments.length : 0;

  if (isEditing) {
    return (
      <div className="flex flex-col rounded-[16px] shadow-md p-[16px] border border-dupontGold gap-y-[12px] bg-dupontIvory dark:bg-darkCard">
        <div className="text-[16px] font-bold text-dupontBlue dark:text-darkText">
          Editing Post
        </div>
        <SharedTextInput
          label="Title*"
          name="edit-title"
          value={editTitle}
          onChange={setEditTitle}
          variant="outlined"
          maxChars={MAX_TITLE_LENGTH}
        />
        <Autocomplete
          options={CATEGORIES}
          value={editCategory}
          onChange={(_, newValue) => setEditCategory(newValue || "other")}
          getOptionLabel={(option) => categoryLabel(option)}
          renderInput={(params) => (
            <TextField {...params} label="Category*" variant="outlined" />
          )}
          size="small"
        />
        <SharedTextInput
          label="Content*"
          name="edit-content"
          value={editContent}
          onChange={setEditContent}
          multiline
          rows={4}
          variant="outlined"
          maxChars={MAX_CONTENT_LENGTH}
        />
        {errorLine}
        <div className="flex flex-row gap-x-[10px]">
          <SharedButton
            disabled={!editTitle || !editContent}
            onClick={handleSaveEdit}
            loadingText={isUpdating ? "Saving..." : undefined}
          >
            Save Changes
          </SharedButton>
          <SharedButton
            color="secondary"
            onClick={() => {
              setActionError(null);
              setIsEditing(false);
            }}
          >
            Cancel
          </SharedButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-[16px] shadow-md p-[16px] border border-dupontGold/30 dark:border-darkGold/20 bg-white dark:bg-darkCard gap-y-[8px]">
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-col gap-y-[4px]">
          <div className="flex flex-row items-center gap-x-[8px] flex-wrap">
            <div className="text-[17px] font-bold text-dupontBlue dark:text-darkText">
              {post.title}
            </div>
            <CategoryTag category={post.category} />
            {post.is_pinned && (
              <span className="text-[11px] font-bold px-[8px] py-[2px] rounded-full bg-dupontGold/20 text-dupontGoldDk">
                Pinned
              </span>
            )}
          </div>
          <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
            by {post.author_name} &middot; {formattedDate}
            {attachmentCount > 0 && (
              <> &middot; {attachmentCount} attachment{attachmentCount !== 1 ? "s" : ""}</>
            )}
          </div>
        </div>
      </div>
      <div className="text-[15px] line-clamp-2 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-dupontBlue dark:text-darkText">
        {post.content}
      </div>
      <div className="flex flex-row gap-x-[10px] pt-[8px] border-t border-dupontGold/30 dark:border-darkGold/20 items-center">
        {showDeleteConfirm ? (
          <div className="flex flex-row items-center gap-x-[10px]">
            <span className="text-[14px] text-red-600">
              Delete this post?
            </span>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-[13px] font-bold text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="text-[13px] font-bold text-dupontGray hover:text-dupontBlue"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={handleStartEdit}
              className="text-[13px] font-bold text-dupontGoldDk hover:text-dupontGold"
            >
              Edit
            </button>
            <button
              onClick={handleTogglePin}
              disabled={isPinning}
              className="text-[13px] font-bold text-dupontBlue hover:text-dupontBlue/70 disabled:opacity-50"
            >
              {isPinning
                ? "Updating..."
                : post.is_pinned
                ? "Unpin"
                : "Pin"}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-[13px] font-bold text-red-400 hover:text-red-600"
            >
              Delete
            </button>
          </>
        )}
      </div>
      {errorLine}
    </div>
  );
}

function BulletinManagement({ individualId }) {
  const { data: posts, isLoading, isFetching, isError, refetch } =
    useGetAdminBulletinPostsQuery(individualId);

  // Also while "Try again" runs: RTK Query then reports neither loading nor
  // error and no data, which would leave the tab blank.
  if (isLoading || (!posts && isFetching)) {
    return <SharedLoading text="Loading posts..." />;
  }

  // A failed load says so, with a retry.
  if (isError && !posts) {
    return <SharedFeatureError title="We couldn't load the posts" onRetry={refetch} />;
  }

  if (!posts) {
    return null;
  }

  if (posts.length === 0) {
    return (
      <div className="text-[15px] text-dupontGray dark:text-darkTextSecondary">
        No bulletin posts yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-[16px]">
      <div className="text-[14px] text-dupontGray dark:text-darkTextSecondary">
        {posts.length} post{posts.length !== 1 ? "s" : ""} total
      </div>
      {posts.map((post) => (
        <PostRow key={post.id} post={post} individualId={individualId} />
      ))}
    </div>
  );
}

export default BulletinManagement;
